import React from "react";
import { TextField, Dropdown, IDropdownOption, PrimaryButton, Stack, DefaultButton } from "@fluentui/react";
import { YourDetailsState } from "./CreateAccountWizard";
import { IEntity } from "../../types";
import { IEntityService } from "../../services";
import { LookupService } from "../../services/LookupService";

interface Props {
    details: YourDetailsState;
    setDetails: (d: YourDetailsState) => void;
    onContinue: () => void;
    onBack: () => void;
    customerCountries: IEntity[];
    customerSectors: IEntity[];
    titleOptions: IDropdownOption[];
}

export const StepYourDetails: React.FC<Props> = ({
    details,
    setDetails,
    onContinue,
    onBack,
    customerCountries,
    customerSectors,
    titleOptions
}) => {
    const [validationErrors, setValidationErrors] = React.useState<Record<string, string>>({});

    const update = (field: keyof YourDetailsState, value: any) => {
        setDetails({
            ...details,
            [field]: value
        });
        // Clear error for this field when user starts editing
        if (validationErrors[field]) {
            setValidationErrors({
                ...validationErrors,
                [field]: ""
            });
        }
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!details.organisationName?.trim()) {
            errors.organisationName = "Organisation Name is required";
        }
        if (!details.address?.trim()) {
            errors.address = "Primary Address is required";
        }
        if (!details.country) {
            errors.country = "Country is required";
        }
        if (!details.sector) {
            errors.sector = "Sector is required";
        }
        if (!details.title) {
            errors.title = "Title is required";
        }
        if (!details.firstname?.trim()) {
            errors.firstname = "Firstname is required";
        }
        if (!details.surname?.trim()) {
            errors.surname = "Surname is required";
        }
        if (!details.email?.trim()) {
            errors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email)) {
            errors.email = "Please enter a valid email address";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleContinue = () => {
        if (validateForm()) {
            onContinue();
        }
    };

    return (
        <div className="step-details-container">

            <h2 className="create-account-heading">Your Details</h2>

            <div className="step-details-section">

                <div className="step-details-subtitle">
                    Your Organisation
                </div>

                <div className="step-details-grid">

                    <div className="step-details-field">
                        <TextField
                            label="Organisation Name"
                            value={details.organisationName}
                            onChange={(_, v) => update("organisationName", v)}
                            errorMessage={validationErrors.organisationName}
                        />
                    </div>

                    <div className="step-details-field">
                        <TextField
                            label="Primary Address"
                            value={details.address}
                            onChange={(_, v) => update("address", v)}
                            errorMessage={validationErrors.address}
                        />
                    </div>

                    <div className="step-details-field">
                        <Dropdown
                            label="Country"
                            options={LookupService.entitiesToSelectableOptions(customerCountries)}
                            selectedKey={details.country}
                            onChange={(_, o) => update("country", o?.key)}
                            errorMessage={validationErrors.country}
                        />
                    </div>

                    <div className="step-details-field">
                        <Dropdown
                            label="Sector"
                            options={LookupService.entitiesToSelectableOptions(customerSectors)}
                            selectedKey={details.sector}
                            onChange={(_, o) => update("sector", o?.key)}
                            errorMessage={validationErrors.sector}
                        />
                    </div>

                    <div className="step-details-field">
                        <TextField
                            label="Number of Staff"
                            value={details.numberOfStaff}
                            onChange={(_, v) => update("numberOfStaff", v)}
                        />
                    </div>

                    <div className="step-details-field">
                        <TextField
                            label="Website"
                            value={details.website}
                            onChange={(_, v) => update("website", v)}
                        />
                    </div>

                    <div className="step-details-field">
                        <TextField
                            label="Organisation Phone"
                            value={details.orgPhone}
                            onChange={(_, v) => update("orgPhone", v)}
                        />
                    </div>

                </div>

            </div>


            <div className="step-details-section">

                <div className="step-details-subtitle">
                    Your Details
                </div>

                <div className="step-details-grid">

                    <div className="step-details-field">
                        <Dropdown
                            label="Title"
                            options={titleOptions}
                            selectedKey={details.title}
                            onChange={(_, o) => update("title", o?.key)}
                            errorMessage={validationErrors.title}
                        />
                    </div>

                    <div className="step-details-field">
                        <TextField
                            label="Firstname"
                            value={details.firstname}
                            onChange={(_, v) => update("firstname", v)}
                            errorMessage={validationErrors.firstname}
                        />
                    </div>

                    <div className="step-details-field">
                        <TextField
                            label="Surname"
                            value={details.surname}
                            onChange={(_, v) => update("surname", v)}
                            errorMessage={validationErrors.surname}
                        />
                    </div>

                    <div className="step-details-field">
                        <TextField
                            label="Email"
                            value={details.email}
                            onChange={(_, v) => update("email", v)}
                            errorMessage={validationErrors.email}
                        />
                    </div>

                    <div className="step-details-field">
                        <TextField
                            label="Phone"
                            value={details.phone}
                            onChange={(_, v) => update("phone", v)}
                        />
                    </div>

                </div>

            </div>

            {/* ACTION BUTTONS */}
            <Stack horizontal tokens={{ childrenGap: 12 }} className="step-actions-btns">
                <DefaultButton text="Back" onClick={onBack} />
                <PrimaryButton text="Continue" onClick={handleContinue} />
            </Stack>

        </div>
    );
};